import { 
  BarChart3, 
  AlertTriangle, 
  CheckCircle, 
  Edit3, 
  TrendingUp,
  Users,
  Hash
} from 'lucide-react';

export default function CalendarSummary({ summary }) {
  if (!summary) return null;

  const getEngagementColor = (potential) => {
    switch (potential?.toLowerCase()) {
      case 'high': return 'text-green-600 bg-green-100';
      case 'medium': return 'text-yellow-600 bg-yellow-100';
      case 'low': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const stats = [
    {
      label: 'Total Posts',
      value: summary.total_posts,
      icon: BarChart3,
      color: 'text-blue-600 bg-blue-100'
    },
    {
      label: 'Avg. Score',
      value: summary.avg_score?.toFixed(1) || 'N/A',
      icon: TrendingUp,
      color: summary.avg_score >= 7 ? 'text-green-600 bg-green-100' : 'text-yellow-600 bg-yellow-100'
    },
    {
      label: 'Warnings',
      value: summary.warnings_count || 0,
      icon: AlertTriangle,
      color: summary.warnings_count > 0 ? 'text-yellow-600 bg-yellow-100' : 'text-green-600 bg-green-100'
    },
    {
      label: 'Edits Pending',
      value: summary.edits_pending || 0,
      icon: Edit3,
      color: 'text-purple-600 bg-purple-100'
    }
  ];

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4">
      <h3 className="font-semibold text-gray-900 mb-4">Calendar Summary</h3>
      
      {/* Stats grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
        {stats.map((stat) => {
          const Icon = stat.icon;
          return (
            <div key={stat.label} className="text-center">
              <div className={`inline-flex items-center justify-center w-10 h-10 rounded-full ${stat.color} mb-2`}>
                <Icon className="w-5 h-5" />
              </div>
              <div className="text-2xl font-bold text-gray-900">{stat.value}</div>
              <div className="text-sm text-gray-500">{stat.label}</div>
            </div>
          );
        })}
      </div>

      {/* Engagement potential */}
      <div className="flex items-center justify-between py-3 border-t border-gray-100">
        <span className="text-sm text-gray-600">Engagement Potential</span>
        <span className={`badge ${getEngagementColor(summary.engagement_potential)}`}>
          {summary.engagement_potential || 'Unknown'}
        </span>
      </div>

      {/* Personas used */}
      {summary.personas_used?.length > 0 && (
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 py-3 border-t border-gray-100">
          <div className="flex items-center gap-2">
            <Users className="w-4 h-4 text-gray-400 flex-shrink-0" />
            <span className="text-sm text-gray-600">Personas</span>
          </div>
          <div className="flex flex-wrap gap-1">
            {summary.personas_used.map((persona) => (
              <span key={persona} className="badge bg-gray-100 text-gray-600 text-xs">
                {persona}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Keywords covered */}
      {summary.keywords_covered?.length > 0 && (
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 py-3 border-t border-gray-100">
          <div className="flex items-center gap-2">
            <Hash className="w-4 h-4 text-gray-400 flex-shrink-0" />
            <span className="text-sm text-gray-600">Keywords</span>
          </div>
          <div className="flex flex-wrap gap-1">
            {summary.keywords_covered.slice(0, 5).map((kw) => (
              <span key={kw} className="badge bg-blue-100 text-blue-600 text-xs">
                {kw}
              </span>
            ))}
            {summary.keywords_covered.length > 5 && (
              <span className="badge bg-gray-100 text-gray-500 text-xs">
                +{summary.keywords_covered.length - 5}
              </span>
            )}
          </div>
        </div>
      )}

      {/* Warnings list */}
      {summary.warnings?.length > 0 && (
        <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
          <div className="flex items-center gap-2 text-yellow-800 text-sm font-medium mb-2">
            <AlertTriangle className="w-4 h-4 flex-shrink-0" />
            <span>Warnings</span>
          </div>
          <ul className="text-sm text-yellow-700 space-y-1">
            {summary.warnings.slice(0, 5).map((warning, idx) => (
              <li key={idx} className="flex items-start gap-1">
                <span>•</span>
                <span>{warning}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Ready indicator */}
      {summary.errors_count === 0 && summary.avg_score >= 7 && (
        <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2">
          <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
          <span className="text-green-800 font-medium">Ready to Post</span>
        </div>
      )}
    </div>
  );
}
